import * as React from 'react';

import { IStock } from '@notino/shared/definitions/custom-definitions';
import {
  CatalogVariantFragmentFragment,
  VariantDisplayType,
} from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import PriceLabel from '@components/PriceLabel';

import { useIsDualPriceForCroatia } from '../../hooks/useIsDualPriceForCroatia';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { TagDiscount } from '../VariantsInSelectBox/TagDiscount';
import { useHasStressOnVoucherDiscount } from '../VoucherDiscount/useHasStressOnVoucherDiscount';

import {
  Container,
  FlexWrapper,
  Name,
  Line,
  LineWrapper,
  PriceWrapper,
  CurrencyWrapper,
  DualPriceSplitter,
  DualPrice,
  DualPriceWrapper,
  SmallerPriceWrapper,
  SmallerCurrencyWrapper,
} from './styled';

export interface ISelectedVariantProps {
  variant: CatalogVariantFragmentFragment;
  displayType: VariantDisplayType;
  singleVariant?: boolean;
  renderTagDiscount: boolean;
  variantName: React.ReactNode;

  hideLine?: boolean;
  hideVariantName?: boolean;
}

export const SelectedVariant: React.FC<ISelectedVariantProps> = React.memo(
  ({
    variant,
    renderTagDiscount,
    variantName,
    hideLine = false,
    hideVariantName = false,
  }) => {
    const newDesign = useNewPdDesignEnabled();
    const {
      isDualPriceForCroatia,
      getLeftSideFormattedPrice,
      getRightSideFormattedPrice,
      leftSideCurrency,
      rightSideCurrency,
    } = useIsDualPriceForCroatia();
    const inStock = variant.stockAvailability.code !== IStock.outOfStock;

    const isSmall = useHasStressOnVoucherDiscount();

    return (
      <ErrorBoundary>
        <Container id="pdSelectedVariant" newDesign={newDesign}>
          {renderTagDiscount && <TagDiscount variant={variant} />}
          <FlexWrapper aria-live="assertive" tabIndex={0} newDesign={newDesign}>
            <Name newDesign={newDesign} isSmall={isSmall}>
              {hideVariantName ? null : variantName}
            </Name>

            {inStock && !isDualPriceForCroatia && (
              <PriceLabel
                price={variant.price}
                wrapperComponent={
                  newDesign
                    ? isSmall
                      ? SmallerPriceWrapper
                      : PriceWrapper
                    : undefined
                }
                currencyComponent={
                  newDesign
                    ? isSmall
                      ? SmallerCurrencyWrapper
                      : CurrencyWrapper
                    : undefined
                }
                id="pd-price"
              />
            )}

            {inStock && isDualPriceForCroatia && (
              <DualPriceWrapper id="pd-price">
                <DualPrice newDesign={newDesign} isSmall={isSmall}>
                  {getLeftSideFormattedPrice(variant.price.value)}{' '}
                  <span>{leftSideCurrency}</span>
                </DualPrice>

                <DualPriceSplitter newDesign={newDesign} isSmall={isSmall}>
                  /
                </DualPriceSplitter>

                <DualPrice newDesign={newDesign} isSmall={isSmall}>
                  {getRightSideFormattedPrice(variant.price.value)}{' '}
                  <span>{rightSideCurrency}</span>
                </DualPrice>
              </DualPriceWrapper>
            )}
          </FlexWrapper>
          {!hideLine && (
            <LineWrapper newDesign={newDesign}>
              <Line
                dashedLine={
                  newDesign ||
                  variant.attributes.IsBlackFridayOffer ||
                  variant.attributes.BlackFridayOfferDiscount ||
                  variant.attributes.GlobalPercentDiscount ||
                  variant.attributes.VoucherDiscount ||
                  variant.attributes.PerfumesDiscount
                }
              />
            </LineWrapper>
          )}
        </Container>
      </ErrorBoundary>
    );
  }
);

SelectedVariant.displayName = 'SelectedVariant';
